import type TxpGriffinSettingsPayload from '../store/schema/TxpGriffinSettingsPayload';
import type TxpSettingsEntryPoint from '../store/schema/TxpSettingsEntryPoint';
import getUserSettingsAction from '../services/getUserSettingsAction';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { trace } from 'owa-trace';
import { mutatorAction } from 'satcheljs';
import type { AccountKey, MailboxInfo } from 'owa-client-types';
import { getAccountKeyForMailboxInfo } from 'owa-client-types';
import { getCalendarConfigurationState } from '../selectors/getCalendarConfigurationState';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { logCoreUsage } from 'owa-analytics';
import { isLocalDataBackedMailboxInfo } from 'owa-account-source-list/lib/utils/isLocalDataBackedAccount';
const reloadPromises = new Map<AccountKey, Promise<void>>();
export default async function reloadTxpSettings(
    shouldLogDatapoint?: boolean,
    entryPoint?: TxpSettingsEntryPoint,
    mailboxInfo: MailboxInfo = getGlobalSettingsAccountMailboxInfo()
): Promise<void> {
    if (isLocalDataBackedMailboxInfo(mailboxInfo)) {
        // PST accounts don't support TXP, so we'll keep txpSettings as null
        return;
    }
    const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
    if (isGlobalFeatureEnabled('cal-txp-optionsV2') && !reloadPromises.has(accountKey)) {
        const reloadPromise = reloadTxpSettingsInternal(
            mailboxInfo,
            shouldLogDatapoint,
            entryPoint
        );
        reloadPromises.set(accountKey, reloadPromise);
    }
    await reloadPromises.get(accountKey);
    reloadPromises.delete(accountKey);
}
async function reloadTxpSettingsInternal(
    mailboxInfo: MailboxInfo,
    shouldLogDatapoint?: boolean,
    entryPoint?: TxpSettingsEntryPoint
) {
    try {
        const response = await getUserSettingsAction(
            shouldLogDatapoint,
            entryPoint,
            undefined /* entityType */,
            mailboxInfo
        );
        const responseStatus = response.status;
        setTxpSettings(
            mailboxInfo,
            responseStatus === 200 ? await response.json() : null,
            responseStatus
        );
        if (responseStatus !== 200) {
            logCoreUsage('TXPSettingsResponseError', mailboxInfo, [
                responseStatus,
                response.statusText,
            ]);
        }
    } catch (e) {
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS18046: 'e' is of type 'unknown'.
        // @ts-expect-error
        trace.warn('[TXP]' + e.message);
        /* eslint-disable owa-custom-rules/forbid-specific-functions-on-catch-finally-blocks -- (https://aka.ms/OWALintWiki)
         * BASELINE. DO NOT COPY AND PASTE!, replace this line with a justification or fix the error, use PerformanceDatapoint instead.
         *	> Do not call 'logCoreUsage' from 'owa-analytics' inside catch or finally blocks */
        logCoreUsage('TXPSettingsResponseError', mailboxInfo, [(e as Error).message]);
        /* eslint-enable owa-custom-rules/forbid-specific-functions-on-catch-finally-blocks */
    }
}
/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
const setTxpSettings = mutatorAction(
    'setTxpSettings',
    (mailboxInfo: MailboxInfo, response: TxpGriffinSettingsPayload, statusCode: number) => {
        getCalendarConfigurationState(mailboxInfo).txpSettings = response;
        getCalendarConfigurationState(mailboxInfo).statusCode = statusCode;
    }
);
